import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Typography,
} from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { ReactComponent as SearchIcon } from '../../../../assets/images/Search.svg';
import { Popup, Pagination, Unauthorized } from '../../../../components/index';
import createAxiosInstance from '../../../../api';
import { useAlert } from '../../../../components/Notification';
import useDebounce from '../../../../components/Debounce';
import { useSelector, useDispatch } from 'react-redux';
import {
  setAllRoleListAction,
  setRoleListAction,
} from '../../../../stores/actionTypes/roleActions';

interface Role {
  roleID?: string | number;
  roleName?: string;
}

interface Filter {
  roleIDs: string[];
}

interface Props {
  pagePermissionIDs: unknown;
}

const RoleManagement: React.FC<Props> = ({ pagePermissionIDs }) => {
  const showAlert = useAlert();
  const navigate = useNavigate();
  const axiosInstance = createAxiosInstance();
  const [roles, setRole] = useState<Role[]>([]);
  const [roleerror, setRoleError] = useState(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number>('');
  const canCreateRoles =
    Array.isArray(pagePermissionIDs) &&
    pagePermissionIDs.includes('roles::create');
  const canDeletePermission =
    Array.isArray(pagePermissionIDs) &&
    pagePermissionIDs.includes('roles::delete');
  const canlistPermission =
    Array.isArray(pagePermissionIDs) &&
    pagePermissionIDs.includes('roles::list');
  const canUpdatePermission =
    Array.isArray(pagePermissionIDs) &&
    pagePermissionIDs.includes('roles::update');
  const dispatch = useDispatch();
  const { roleList } = useSelector((state: any) => state.role);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getData(page, rowsPerPage, search);
  };

  const handleRowsPerPageChange = (value: number) => {
    getData(currentPage - 1, value, search);
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = () => {
    setCurrentPage(1);
    axiosInstance
      .post('/deleteRole', { roleID: deleteId })
      .then(() => {
        getData(1);
        getRoleData();
        setDeletePopup(false);
        showAlert('Role deleted successfully', 'success');
      })
      .catch(() => {
        showAlert('Failed to delete role', 'error');
      });
  };

  const getRoleData = async () => {
    await axiosInstance
      .post('/listRole', {})
      .then((response) => {
        if (response?.data?.data) {
          dispatch(setAllRoleListAction(response?.data?.data));
        }
      })
      .catch(() => {
        dispatch(setAllRoleListAction([]));
      });
  };

  const handleSearchChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const search = event.target.value;
    setSearch(search);

    if (!search) {
      getData(currentPage, rowsPerPage, search);
    }
  };

  // const handleChange = (roleid: any) => {
  //   if (roleid) {
  //     const index = filterid.indexOf(roleid);
  //     if (index === -1) {
  //       const addroleid = [...filterid, roleid];
  //       setfilterid(addroleid);
  //       getData(currentPage, rowsPerPage, search, addroleid);
  //     } else {
  //       const removeRoleId = filterid.filter((id: any) => id !== roleid);
  //       setfilterid(removeRoleId);
  //       getData(currentPage, rowsPerPage, search, removeRoleId);
  //     }
  //   }
  // };

  const getData = async (
    pagenumber?: number,
    row_perpage?: number,
    search: unknown = '',
    filters: Filter[] | undefined = [],
    sort: { orderBy: string; order: number } = {
      orderBy: 'createdAt',
      order: -1,
    },
  ) => {
    const setfilter = filters?.length > 0 ? { roleIDs: filters } : {};
    await axiosInstance
      .post('/listRole', {
        search: search,
        sort: sort,
        filters: setfilter,
        limit: row_perpage ? row_perpage : rowsPerPage,
        offset: pagenumber
          ? (pagenumber - 1) * rowsPerPage
          : (currentPage - 1) * rowsPerPage,
      })
      .then((response) => {
        dispatch(setRoleListAction(response?.data?.data));
        setRole(response.data.data);
        setTotalPages(
          Math.ceil(
            response.data.count / (row_perpage ? row_perpage : rowsPerPage),
          ),
        );
        setRoleError(false);
      })
      .catch(() => {
        dispatch(setRoleListAction([]));
        setRole([]);
        setRoleError(true);
      });
  };

  useEffect(() => {
    getData();
    getRoleData();
  }, []);

  useEffect(() => {
    if (debouncedSearch) {
      getData(currentPage, rowsPerPage, search);
    }
  }, [debouncedSearch]);

  if (roleerror) {
    return <Unauthorized />;
  }

  return (
    <>
      {!canlistPermission ? (
        <Unauthorized />
      ) : (
        <>
          {deletePopup && (
            <Popup
              open={deletePopup}
              handleClose={() => {
                setDeletePopup(false);
              }}
              handleDelete={() => {
                handleDelete();
              }}
              page="role"
            />
          )}
          <div className="content rolemanagement">
            <div className="rolemanagement-header d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3">
              <div className="d-flex justify-content-center align-items-center">
                <div>
                  <Typography variant="h5" className="h5 table-header">
                    <b>Roles</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    className="table-header-instruction m-0"
                  >
                    View and update your Roles
                  </Typography>
                </div>
              </div>

              <div className="table-header-btn">
                <TextField
                  variant="outlined"
                  placeholder="Search"
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: <SearchIcon />,
                  }}
                  className="search-input"
                />
                {canCreateRoles && (
                  <Button
                    className="create-btn"
                    color="primary"
                    onClick={() => navigate('/iam/role/create')}
                  >
                    Create Role
                  </Button>
                )}
              </div>
            </div>

            <TableContainer component={Paper} className="roleTable_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body1">Role Name</Typography>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="body1" align="left">
                          No Data found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    roles &&
                    roles.map((role) => (
                      <TableRow key={role.roleID}>
                        <TableCell>
                          {role.roleName}
                          {/* <IconButton color="secondary" size="small">
                            <ArrowRightAltIcon
                              className="form-label-arrow"
                              color="action"
                            />
                          </IconButton> */}
                        </TableCell>
                        <TableCell className="form-action-btn" align="right">
                          {canUpdatePermission && (
                            <Button
                              className="btn-transparent border-0 img-background-form"
                              onClick={() =>
                                navigate(
                                  `/iam/role/edit/${role.roleName}/${role.roleID}`,
                                )
                              }
                            >
                              <i className="edit-icon bi bi-pencil-square bi-lg"></i>
                            </Button>
                          )}
                          {canDeletePermission && (
                            <Button
                              className="btn-transparent border-0 img-background-form"
                              onClick={() => {
                                setDeleteId(role.roleID ? role.roleID : '');
                                setDeletePopup(true);
                              }}
                            >
                              <i className="delete-icon bi bi-trash3-fill bi-lg"></i>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              rowsPerPageOptions={[5, 10, 20, 30]}
              currentPage={currentPage}
              onPageChange={(page) => handlePageChange(page)}
              onRowsPerPageChange={handleRowsPerPageChange}
              totalPages={totalPages}
            />
          </div>
        </>
      )}
    </>
  );
};

export default RoleManagement;
